import type { PolicyEvaluateResponse } from '@/models/flw-orchestrator.types';
import type { PolicyEvaluationParams } from './policyEvaluationService';
import { logDev as debugLog } from '@/logging/devConsole';

interface CachedPolicy {
  result: PolicyEvaluateResponse;
  timestamp: number;
}

/**
 * PolicyCacheService - Caches MFA and MAM policy evaluation results
 * Reduces redundant API calls and improves performance
 */
export class PolicyCacheService {
  private static mfaCache = new Map<string, CachedPolicy>();
  private static mamCache = new Map<string, CachedPolicy>();
  private static readonly TTL = 3600000; // 1 hour in milliseconds

  /**
   * Generate a unique cache key based on user and device parameters
   */
  private static getCacheKey(params: PolicyEvaluationParams): string {
    return `${params.userId}_${params.devicePlatform}_${params.applicationId}_${params.env}_${params.region}`;
  }

  /**
   * Get cached MFA result if available and not expired
   */
  static getMfaCache(params: PolicyEvaluationParams): PolicyEvaluateResponse | null {
    const key = this.getCacheKey(params);
    const cached = this.mfaCache.get(key);
    
    if (cached && Date.now() - cached.timestamp < this.TTL) {
      debugLog('PolicyCacheService: MFA cache hit for key:', key);
      return cached.result;
    }
    
    if (cached) {
      debugLog('PolicyCacheService: MFA cache expired for key:', key);
      this.mfaCache.delete(key);
    }
    
    return null;
  }

  /**
   * Cache MFA result
   */
  static setMfaCache(params: PolicyEvaluationParams, result: PolicyEvaluateResponse): void {
    const key = this.getCacheKey(params);
    this.mfaCache.set(key, {
      result,
      timestamp: Date.now()
    });
    debugLog('PolicyCacheService: MFA result cached for key:', key);
  }

  /**
   * Get cached MAM result if available and not expired
   */
  static getMamCache(params: PolicyEvaluationParams): PolicyEvaluateResponse | null {
    const key = this.getCacheKey(params);
    const cached = this.mamCache.get(key);
    
    if (cached && Date.now() - cached.timestamp < this.TTL) {
      debugLog('PolicyCacheService: MAM cache hit for key:', key);
      return cached.result;
    }
    
    if (cached) {
      debugLog('PolicyCacheService: MAM cache expired for key:', key);
      this.mamCache.delete(key);
    }
    
    return null;
  }

  /**
   * Cache MAM result
   */
  static setMamCache(params: PolicyEvaluationParams, result: PolicyEvaluateResponse): void {
    const key = this.getCacheKey(params);
    this.mamCache.set(key, {
      result,
      timestamp: Date.now()
    });
    debugLog('PolicyCacheService: MAM result cached for key:', key);
  }

  /**
   * Clear all cached policy results
   * Should be called on logout or when user context changes
   */
  static clearCache(): void {
    debugLog('PolicyCacheService: Clearing all cached policy results');
    this.mfaCache.clear();
    this.mamCache.clear();
  }

  /**
   * Clear MFA cache only
   */
  static clearMfaCache(): void {
    debugLog('PolicyCacheService: Clearing MFA cache');
    this.mfaCache.clear();
  }

  /**
   * Clear MAM cache only
   */
  static clearMamCache(): void {
    debugLog('PolicyCacheService: Clearing MAM cache');
    this.mamCache.clear();
  }

  /**
   * Get cache statistics for debugging
   */
  static getCacheStats(): { mfaCacheSize: number; mamCacheSize: number } {
    return {
      mfaCacheSize: this.mfaCache.size,
      mamCacheSize: this.mamCache.size
    };
  }
}
